// Ruta: frontend/src/modules/checklists/pages/ChecklistEditPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useChecklist } from '@core/hooks/useChecklist';
import { Button } from '@shared/components/Button';
import { Input } from '@shared/components/Input';
import { Textarea } from '@shared/components/Textarea';
import { Question } from '@core/types';

const ChecklistEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getChecklistById, updateChecklist } = useChecklist();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;
    const loadChecklist = async () => {
      try {
        const data = await getChecklistById(id);
        setTitle(data.title);
        setDescription(data.description || '');
        // ✅ Asegurar que questions sea siempre un array
        setQuestions(data.questions || []);
      } catch (err) {
        alert('Error al cargar el checklist');
        navigate('/checklists');
      } finally {
        setIsLoading(false);
      }
    };
    loadChecklist();
  }, [id]);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { id: `temp-${Date.now()}`, text: '', type: 'TEXT', order: questions.length, required: false, options: [] },
    ]);
  };

  const removeQuestion = (index: number) => {
    if (questions.length === 1) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (questions.some((q) => !q.text.trim())) {
      alert('Todas las preguntas deben tener texto');
      return;
    }

    setIsSubmitting(true);
    try {
      await updateChecklist(id!, {
        title: title.trim(),
        description: description.trim() || undefined,
        questions: questions.map((q, i) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          options: q.type === 'CHOICE' ? q.options || [] : undefined,
          order: i,
          required: q.required,
        })),
      });
      navigate('/checklists');
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Cargando...</div>;
  }

  const questionTypeOptions = [
    { value: 'TEXT', label: 'Texto' },
    { value: 'NUMBER', label: 'Número' },
    { value: 'BOOLEAN', label: 'Sí/No' },
    { value: 'CHOICE', label: 'Opción múltiple' },
    { value: 'DATE', label: 'Fecha' },
    { value: 'PHOTO', label: 'Foto' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Editar Checklist</h1>
        <div className="space-x-2">
          <Button variant="secondary" onClick={() => navigate('/checklists')}>
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting}>
            Guardar cambios
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <Input
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
        <Textarea
          label="Descripción"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900">Preguntas</h2>
          <Button variant="secondary" size="sm" onClick={addQuestion}>
            + Agregar pregunta
          </Button>
        </div>

        {/* ✅ Protección: si questions está vacío o undefined, mostrar mensaje */}
        {!questions || questions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No hay preguntas. Agrega una.</p>
        ) : (
          questions.map((question, index) => (
            <div key={question.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm font-medium text-gray-500">Pregunta {index + 1}</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => removeQuestion(index)}
                  disabled={questions.length === 1}
                >
                  Eliminar
                </Button>
              </div>

              <Input
                value={question.text}
                onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                placeholder="Texto de la pregunta"
                required
              />

              <div className="grid grid-cols-2 gap-3">
                <select
                  value={question.type}
                  onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                  className="rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  {questionTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={question.required}
                    onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Obligatoria</span>
                </label>
              </div>

              {question.type === 'CHOICE' && (
                <div className="space-y-2">
                  <Input
                    value={question.options?.join(', ') || ''}
                    onChange={(e) =>
                      updateQuestion(
                        index,
                        'options',
                        e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                      )
                    }
                    placeholder="Opciones separadas por coma: Ej: Sí, No, N/A"
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </form>
  );
};

export default ChecklistEditPage;